import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW, GLASS } from '../shared/theme'
import { fadeIn, entrance, staggerDelay, breathe, pulse, SPRINGS } from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * ListReveal — Lista animada con checkmarks/bullets uno a uno.
 *
 * params: { items: [string], style: 'check'|'bullet'|'number', title, accent_color }
 */

const CHECKMARK_PATH_LEN = 28 // approximate polyline length for "4,14 11,21 24,8"

function CheckIcon({ progress, color }) {
  const drawn = progress * CHECKMARK_PATH_LEN
  return (
    <svg width={32} height={32} viewBox="0 0 32 32" style={{ flexShrink: 0 }}>
      {/* Circle background */}
      <circle cx={16} cy={16} r={14} fill={`${color}18`} stroke={`${color}35`} strokeWidth={1.5} />
      {/* Checkmark stroke */}
      <polyline
        points="8,16 13,22 24,10"
        fill="none"
        stroke={color}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={CHECKMARK_PATH_LEN}
        strokeDashoffset={CHECKMARK_PATH_LEN - drawn}
      />
    </svg>
  )
}

function BulletIcon({ scale, color }) {
  return (
    <div
      style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        background: color,
        transform: `scale(${scale})`,
        boxShadow: GLOW.sm(color),
        flexShrink: 0,
        marginTop: 2,
      }}
    />
  )
}

function NumberIcon({ number, scale, color }) {
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: `${color}20`,
        border: `1.5px solid ${color}50`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `scale(${scale})`,
        flexShrink: 0,
        boxShadow: GLOW.sm(color),
      }}
    >
      <span
        style={{
          fontFamily: FONTS.heading,
          fontSize: 14,
          fontWeight: 700,
          color,
        }}
      >
        {number}
      </span>
    </div>
  )
}

export default function ListReveal({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const { items = [], style = 'check', title = '', accent_color = COLORS.accent.green } = params

  const count = Math.min(items.length, 8)
  const staggerFrames = 14
  const startDelay = 15

  const titleOpacity = fadeIn(frame, 0, 18)
  const titleY = interpolate(frame, [0, 20], [20, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const fontSize = count <= 5 ? 28 : count <= 7 ? 24 : 20
  const itemHeight = fontSize * 2.2

  return (
    <SceneWrapper accent={accent_color} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 200px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 900,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Title */}
          {title ? (
            <div
              style={{
                opacity: titleOpacity,
                transform: `translateY(${titleY}px)`,
                fontFamily: FONTS.heading,
                fontSize: 32,
                fontWeight: 700,
                color: COLORS.text.secondary,
                marginBottom: 24,
                letterSpacing: '-0.01em',
              }}
            >
              {title}
            </div>
          ) : null}

          {/* List items */}
          {items.slice(0, count).map((item, i) => {
            const delay = startDelay + staggerDelay(i, staggerFrames)
            const itemOpacity = fadeIn(frame, delay, 12)
            const itemX = interpolate(frame, [delay, delay + 14], [-32, 0], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })
            const iconProgress = entrance(frame, fps, delay, SPRINGS.snappy)
            const glowPulse = pulse(frame, 0.05 + i * 0.008, 0.6, 1.0)
            const isVisible = frame >= delay

            // Per-item color shift for visual richness
            const PALETTE = [
              COLORS.accent.green,
              COLORS.accent.teal,
              COLORS.accent.blue,
              COLORS.accent.purple,
              COLORS.accent.orange,
            ]
            const itemColor = style === 'check' ? accent_color : PALETTE[i % PALETTE.length]

            return (
              <div
                key={i}
                style={{
                  opacity: itemOpacity,
                  transform: `translateX(${itemX}px)`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 20,
                  minHeight: itemHeight,
                  paddingTop: 4,
                }}
              >
                {/* Icon */}
                {style === 'check' && (
                  <CheckIcon progress={isVisible ? iconProgress : 0} color={itemColor} />
                )}
                {style === 'bullet' && (
                  <div style={{ paddingTop: (itemHeight - 12) / 2 - 4 }}>
                    <BulletIcon scale={iconProgress} color={itemColor} />
                  </div>
                )}
                {style === 'number' && (
                  <NumberIcon number={i + 1} scale={iconProgress} color={itemColor} />
                )}

                {/* Text */}
                <div
                  style={{
                    fontFamily: FONTS.body,
                    fontSize,
                    fontWeight: 500,
                    color: COLORS.text.primary,
                    lineHeight: 1.4,
                    paddingTop: style === 'bullet' ? (itemHeight - fontSize * 1.4) / 2 - 2 : 0,
                  }}
                >
                  {item}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </SceneWrapper>
  )
}
